export default class CoreAudio extends HTMLElement {
	constructor() {
		super();

		this.isRequestedToPlay = false;

		this.audioBuffer = null;
		this.waveformPeaks = [];
		this.isSeeking = false;
		this.rafId = null;
		this.resizeObserver = null;

		this.onButtonClick = this.onButtonClick.bind(this);
		this.onMuteClick = this.onMuteClick.bind(this);
		this.onAudioPlay = this.onAudioPlay.bind(this);
		this.onAudioPause = this.onAudioPause.bind(this);
		this.onAudioEnded = this.onAudioEnded.bind(this);
		this.onAudioTimeUpdate = this.onAudioTimeUpdate.bind(this);
		this.onAudioVolumeChange = this.onAudioVolumeChange.bind(this);
		this.onWaveformPointerDown = this.onWaveformPointerDown.bind(this);
		this.onWaveformPointerMove = this.onWaveformPointerMove.bind(this);
		this.onWaveformPointerUp = this.onWaveformPointerUp.bind(this);
		this.onSliderInput = this.onSliderInput.bind(this);
		this.onSliderChange = this.onSliderChange.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.drawWaveform = this.drawWaveform.bind(this);
	}

	connectedCallback() {
		this.$button = this.querySelector('[audio-button]');
		this.$mute = this.querySelector('[audio-mute]');
		this.$pauseOnly = this.querySelector('[audio-pause]');
		this.$playOnly = this.querySelector('[audio-play]:not([audio-button])');
		this.$audio = this.querySelector('audio[audio-track], [audio-track]');
		this.$waveform = this.querySelector('[audio-waveform]');
		this.$slider = this.querySelector('input[type="range"][audio-slider], [audio-slider]');
		this.$timeCurrent = this.querySelector('[audio-current-time]');
		this.$timeTotal = this.querySelector('[audio-duration]');
		this.$status = this.querySelector('[audio-status]');

		if (!this.$button || !this.$audio) {
			return;
		}

		this.$button.setAttribute('type', 'button');
		this.$button.setAttribute('aria-pressed', 'false');

		this.$button.addEventListener('click', this.onButtonClick);
		this.$pauseOnly?.addEventListener('click', () => this.pause?.());
		this.$playOnly?.addEventListener('click', () => this.play?.());
		this.$audio.addEventListener('play', this.onAudioPlay);
		this.$audio.addEventListener('pause', this.onAudioPause);
		this.$audio.addEventListener('ended', this.onAudioEnded);

		this.$audio.addEventListener('timeupdate', this.onAudioTimeUpdate);
		this.$audio.addEventListener('loadedmetadata', this.onAudioTimeUpdate);
		this.$audio.addEventListener('volumechange', this.onAudioVolumeChange);

		if (this.$slider) {
			this.$slider.setAttribute('min', '0');
			this.$slider.setAttribute('max', '100');
			this.$slider.setAttribute('step', this.$slider.getAttribute('step') || '0.1');
			this.$slider.value = '0';
			if (!this.$slider.hasAttribute('aria-label') && !this.$slider.hasAttribute('aria-labelledby')) {
				this.$slider.setAttribute('aria-label', 'Audio progress');
			}
			this.$slider.setAttribute('aria-valuetext', '00:00 of 00:00');
			this.$slider.disabled = true;
			this.$slider.addEventListener('input', this.onSliderInput);
			this.$slider.addEventListener('change', this.onSliderChange);
		}

		if (this.$mute) {
			this.$mute.setAttribute('type', 'button');
			this.$mute.setAttribute('aria-pressed', this.$audio.muted ? 'true' : 'false');
			this.$mute.addEventListener('click', this.onMuteClick);
			this.$mute.classList.toggle('is-muted', this.$audio.muted);
		}

		this.setupWaveform();
		this.updateState();

		// Optional: word-by-word transcript reveal driven by a timing JSON.
		// The host element opts in via `data-audio-timings-url="..."`. The
		// transcript target element (where the words get rendered) is found
		// by walking up from <core-audio> until we hit an element that
		// contains a [data-audio-transcript] descendant outside this
		// component, OR by searching the closest section.
		const timingsUrl = this.getAttribute('data-audio-timings-url');
		if (timingsUrl) {
			this._setupTimings(timingsUrl);
		}
	}

	_findTranscriptTarget() {
		// Look at siblings/ancestors first (the transcript element typically
		// lives in the same overlay row, not inside <core-audio>).
		let scope = this.parentElement;
		while (scope && scope !== document.body) {
			const candidate = scope.querySelector('[data-audio-transcript]');
			if (candidate) return candidate;
			scope = scope.parentElement;
		}
		return null;
	}

	async _setupTimings(url) {
		this.$transcript = this._findTranscriptTarget();
		if (!this.$transcript) return;

		let words = [];
		try {
			const res = await fetch(url, { cache: 'no-store' });
			if (!res.ok) return;
			const data = await res.json();
			if (Array.isArray(data)) words = data;
			else if (Array.isArray(data?.words)) words = data.words;
		} catch (e) {
			return;
		}

		const allWords = words
			.filter(w => w && typeof w.word === 'string' && Number.isFinite(w.start))
			.map(w => ({
				word: w.word,
				start: Number(w.start),
				end: Number.isFinite(w.end) ? Number(w.end) : Number(w.start),
			}));

		// Match the editor-provided "Sentence" text against the JSON to find
		// the corresponding contiguous run of timed words. Audio playback is
		// restricted to that range — unless data-audio-full-playback is on,
		// in which case the full transcript is revealed and audio plays through.
		const sentenceText = (this.getAttribute('data-transcript-sentence') || '').trim();
		const useFullTimeline = this.getAttribute('data-audio-full-playback') === 'true';
		const norm = (s) => s.replace(/[^\w]/g, '').toLowerCase();
		const sentenceTokens = sentenceText && !useFullTimeline
			? sentenceText.split(/\s+/).map(norm).filter(Boolean)
			: [];

		let startIdx = 0;
		let endIdx = allWords.length - 1;

		if (sentenceTokens.length) {
			let bestStart = -1;
			let bestLen = 0;
			for (let i = 0; i <= allWords.length - 1; i++) {
				let len = 0;
				let j = 0;
				let k = i;
				while (j < sentenceTokens.length && k < allWords.length) {
					if (norm(allWords[k].word) === sentenceTokens[j]) {
						len++;
						j++;
						k++;
					} else {
						break;
					}
				}
				if (len > bestLen) {
					bestLen = len;
					bestStart = i;
					if (len === sentenceTokens.length) break;
				}
			}
			if (bestStart >= 0 && bestLen > 0) {
				startIdx = bestStart;
				endIdx = bestStart + bestLen - 1;
			}
		} else if (!useFullTimeline) {
			// No sentence configured and not in full-timeline mode — fall back
			// to first sentence terminator.
			const sentenceEndRe = /[.!?]['"’”\)\]]*$/;
			let found = -1;
			for (let i = 0; i < allWords.length; i++) {
				if (sentenceEndRe.test(allWords[i].word)) { found = i; break; }
			}
			if (found >= 0) endIdx = found;
		}
		// In full-timeline mode with no sentence (or unmatched sentence) we
		// leave startIdx=0 / endIdx=last so the entire transcript is revealed.

		if (endIdx < startIdx) endIdx = allWords.length - 1;
		this._timingWords = allWords.slice(startIdx, endIdx + 1);

		if (!this._timingWords.length) return;

		const totalDuration = this._timingWords[this._timingWords.length - 1].end + 0.6;
		const fullPlayback = this.getAttribute('data-audio-full-playback') === 'true';

		// Find the time when the suggestion should pop in: the start time of
		// the first word matching the configured trigger phrase.
		const suggestionTrigger = (this.getAttribute('data-suggestion-trigger') || '').trim();
		const findSuggestionTime = () => {
			if (!suggestionTrigger) return null;
			const norm = (s) => s.replace(/[^\w]/g, '').toLowerCase();
			const tokens = suggestionTrigger.split(/\s+/).map(norm).filter(Boolean);
			if (!tokens.length) return null;
			for (let i = 0; i <= this._timingWords.length - tokens.length; i++) {
				let ok = true;
				for (let j = 0; j < tokens.length; j++) {
					if (norm(this._timingWords[i + j].word) !== tokens[j]) { ok = false; break; }
				}
				if (ok) return this._timingWords[i + tokens.length - 1].end;
			}
			return null;
		};
		this._suggestionRevealAt = findSuggestionTime();
		this._$suggestion = (() => {
			let scope = this.parentElement;
			while (scope && scope !== document.body) {
				const el = scope.querySelector('[data-audio-suggestion]');
				if (el) return el;
				scope = scope.parentElement;
			}
			return null;
		})();

		const mode = this.getAttribute('data-transcript-mode') || 'reveal';
		this._sentenceFullText = this.$transcript.textContent.trim();
		this._timingShownCount = 0;

		if (mode === 'scroll') {
			// Pre-render the full sentence as word spans so we can translate
			// the inner track to keep the current word visible.
			this.$transcript.textContent = '';
			this.$transcript.style.overflow = 'hidden';
			this.$transcript.style.whiteSpace = 'nowrap';
			this.$transcript.style.display = 'block';
			this.$transcript.style.position = 'relative';

			const track = document.createElement('span');
			track.style.display = 'inline-block';
			track.style.whiteSpace = 'nowrap';
			track.style.willChange = 'transform';
			track.style.transition = 'transform 0.25s ease-out';
			this._tickerWordEls = this._timingWords.map((w, i) => {
				const el = document.createElement('span');
				el.textContent = w.word + (i < this._timingWords.length - 1 ? ' ' : '');
				track.appendChild(el);
				return el;
			});
			this.$transcript.appendChild(track);
			this._tickerTrack = track;

			this._renderTranscriptAt = (t) => {
				if (this._$suggestion
					&& this._suggestionRevealAt != null
					&& t >= this._suggestionRevealAt
					&& !this._$suggestion.classList.contains('is-revealed')) {
					this._$suggestion.classList.add('is-revealed');
				}
				if (!this._tickerWordEls || !this._tickerWordEls.length) return;
				let activeIdx = 0;
				for (let i = 0; i < this._timingWords.length; i++) {
					if (this._timingWords[i].start <= t) activeIdx = i;
					else break;
				}
				const trackW = this._tickerTrack.scrollWidth || this._tickerTrack.offsetWidth || 0;
				const windowW = this.$transcript.clientWidth || 0;
				// Only scroll once the track is wider than the visible window;
				// otherwise leave it at 0 so the sentence sits flush left.
				if (trackW <= windowW) {
					this._tickerTrack.style.transform = 'translateX(0)';
					return;
				}
				const activeEl = this._tickerWordEls[activeIdx];
				if (!activeEl) return;
				// Aim to keep the active word about 1/4 from the left of the window.
				const targetLeft = Math.max(0, activeEl.offsetLeft - windowW * 0.25);
				const maxLeft = trackW - windowW;
				const offset = Math.min(targetLeft, maxLeft);
				this._tickerTrack.style.transform = `translateX(${-offset}px)`;
			};
		} else {
			// Reveal mode: hide the sentence text until time advances, then
			// type it out word-by-word as the audio plays.
			this.$transcript.textContent = '';

			this._renderTranscriptAt = (t) => {
				if (this._$suggestion
					&& this._suggestionRevealAt != null
					&& t >= this._suggestionRevealAt
					&& !this._$suggestion.classList.contains('is-revealed')) {
					this._$suggestion.classList.add('is-revealed');
				}
				if (!this._timingWords) return;
				let upto = 0;
				for (let i = 0; i < this._timingWords.length; i++) {
					if (this._timingWords[i].start <= t) upto = i + 1;
					else break;
				}
				if (upto === this._timingShownCount) return;
				this._timingShownCount = upto;
				const text = this._timingWords.slice(0, upto).map(w => w.word).join(' ');
				this.$transcript.textContent = text;
			};
		}

		// Timer-driven timeline using absolute audio times. Starts at the
		// first word's `start` and runs through totalDuration.
		const timelineStart = this._timingWords[0].start;
		this._timelineStart = timelineStart;
		this._timelineT = timelineStart;
		this._timelinePaused = true;       // start paused — release on intersect
		this._timelineLast = null;
		this._tickerStarted = false;

		// Opt-in (block-article-audio): make the <audio> element the single clock.
		// While audio plays we READ its currentTime to drive the transcript and
		// never write currentTime back. The per-frame `audio.currentTime = …`
		// drift seek is especially destructive on iOS Safari, where re-seeking a
		// playing element clicks/stutters/restarts. Other blocks keep the
		// wall-clock timeline + drift correction unchanged.
		const audioDriven = this.getAttribute('data-audio-clock') === 'audio';

		const tick = (now) => {
			if (this._timelineLast === null) this._timelineLast = now;
			const dt = (now - this._timelineLast) / 1000;
			this._timelineLast = now;
			if (!this._timelinePaused) {
				const audioPlaying = this.$audio && !this.$audio.paused;
				if (audioDriven && audioPlaying) {
					// Follow the audio clock instead of advancing by wall-clock.
					this._timelineT = this.$audio.currentTime;
				} else {
					this._timelineT += dt;
				}
				if (this._timelineT >= totalDuration) {
					if (fullPlayback) {
						this._renderTranscriptAt(this._timelineT);
					} else {
						this._timelineT = totalDuration;
						this._timelinePaused = true;
						this._renderTranscriptAt(this._timelineT);
						if (this.$audio) {
							try { this.$audio.pause(); } catch (e) {}
						}
					}
				} else {
					this._renderTranscriptAt(this._timelineT);
					if (!audioDriven && this.$audio && !this.$audio.paused) {
						const drift = Math.abs((this.$audio.currentTime || 0) - this._timelineT);
						if (drift > 0.35) {
							try { this.$audio.currentTime = this._timelineT; } catch (e) {}
						}
					}
				}
				// Keep the waveform progress fill in sync with the timeline
				// (even when audio is paused — see getProgress()).
				this.drawWaveform();
			}
			this._timelineRaf = requestAnimationFrame(tick);
		};
		this._timelineRaf = requestAnimationFrame(tick);

		this._origPlay = this.play?.bind(this);
		this._origPause = this.pause?.bind(this);

		this.play = () => {
			// Always replay from the beginning when the user hits play, with
			// the audio on (default unmuted).
			this._timelineT = this._timelineStart;
			this._timingShownCount = 0;
			if (this.$transcript && mode !== 'scroll') this.$transcript.textContent = '';
			if (this._$suggestion) this._$suggestion.classList.remove('is-revealed');
			this._timelinePaused = false;
			this._timelineLast = null;
			this.isRequestedToPlay = true;
			if (this.$audio) {
				this.$audio.muted = false;
				if (this.$mute) {
					this.$mute.classList.remove('is-muted');
					this.$mute.setAttribute('aria-pressed', 'false');
				}
				try { this.$audio.currentTime = this._timelineStart; } catch (e) {}
				const p = this.$audio.play();
				if (p && typeof p.then === 'function') {
					p.then(() => this.updateState(true))
					 .catch(() => this.updateState(false));
				} else {
					this.updateState(true);
				}
			} else {
				this.updateState(true);
			}
		};
		this.pause = () => {
			this._timelinePaused = true;
			if (this.$audio) {
				try { this.$audio.pause(); } catch (e) {}
			}
			this._origPause && this._origPause();
		};

		// Auto-start the text type-out (silently — no audio) the first time
		// the block scrolls into view. Audio stays paused; the user clicks
		// play to replay everything with sound.
		this._tickerIO = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && !this._tickerStarted) {
					this._tickerStarted = true;
					this._timelinePaused = false;
					this._timelineLast = null;
					this._tickerIO.disconnect();
					break;
				}
			}
		}, { threshold: 0.25 });
		this._tickerIO.observe(this);
	}

	updateSlider() {
		if (!this.$slider || !this.$audio) {
			return;
		}

		const duration = this.$audio.duration;

		if (!duration || Number.isNaN(duration)) {
			this.$slider.disabled = true;
			this.$slider.value = '0';
			this.$slider.setAttribute('aria-valuetext', '00:00 of 00:00');
			return;
		}

		this.$slider.disabled = false;

		const value = this.getProgress() * 100;
		this.$slider.value = String(value);

		const currentTime = this._timelineT !== undefined && this._timelineT !== null
			? this._timelineT
			: this.$audio.currentTime;

		this.$slider.setAttribute(
			'aria-valuetext',
			`${this.formatTime(currentTime)} of ${this.formatTime(duration)}`
		);
	}

	seekFromSlider() {
		if (!this.$slider || !this.$audio || !this.$audio.duration || Number.isNaN(this.$audio.duration)) {
			return;
		}
		const value = Number(this.$slider.value);
		const progress = Math.min(1, Math.max(0, value / 100));
		const time = progress * this.$audio.duration;
		this.$audio.currentTime = time;
		if (this._timelineT !== undefined && this._timelineT !== null) {
			this._timelineT = time;
		}
		this.updateWaveformA11y();
		this.drawWaveform();
		this.updateSlider();
	}

	onSliderInput() {
		this.isSeeking = true;
		this.seekFromSlider();
	}

	onSliderChange() {
		this.isSeeking = false;
		this.seekFromSlider();
	}

	disconnectedCallback() {
		this.$button?.removeEventListener('click', this.onButtonClick);
		this.$mute?.removeEventListener('click', this.onMuteClick);
		this.$audio?.removeEventListener('play', this.onAudioPlay);
		this.$audio?.removeEventListener('pause', this.onAudioPause);
		this.$audio?.removeEventListener('ended', this.onAudioEnded);
		this.$audio?.removeEventListener('timeupdate', this.onAudioTimeUpdate);
		this.$audio?.removeEventListener('loadedmetadata', this.onAudioTimeUpdate);
		this.$audio?.removeEventListener('volumechange', this.onAudioVolumeChange);

		this.$slider?.removeEventListener('input', this.onSliderInput);
		this.$slider?.removeEventListener('change', this.onSliderChange);

		this.$waveform?.removeEventListener('pointerdown', this.onWaveformPointerDown);
		this.$waveform?.removeEventListener('pointermove', this.onWaveformPointerMove);
		this.$waveform?.removeEventListener('pointerup', this.onWaveformPointerUp);
		this.$waveform?.removeEventListener('pointercancel', this.onWaveformPointerUp);
		this.$waveform?.removeEventListener('keydown', this.onKeyDown);

		this.resizeObserver?.disconnect();
		this._tickerIO?.disconnect();

		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
		}
		if (this._timelineRaf) {
			cancelAnimationFrame(this._timelineRaf);
			this._timelineRaf = null;
		}
	}

	onButtonClick() {
		if (!this.$audio) {
			return;
		}

		if (this.$audio.paused) {
			this.play?.();
		} else {
			this.pause?.();
		}
	}

	onMuteClick() {
		if (!this.$audio || !this.$mute) {
			return;
		}
		const muted = !this.$audio.muted;
		this.$audio.muted = muted;
		this.$mute.classList.toggle('is-muted', muted);
		this.$mute.setAttribute('aria-pressed', muted ? 'true' : 'false');
		// Direct SVG swap fallback (first = speaker, second = speaker with slash).
		const svgs = this.$mute.querySelectorAll(':scope > svg');
		if (svgs.length >= 2) {
			svgs[0].style.display = muted ? 'none' : 'inline-block';
			svgs[1].style.display = muted ? 'inline-block' : 'none';
		}
	}

	onAudioVolumeChange() {
		if (!this.$mute || !this.$audio) {
			return;
		}
		const muted = this.$audio.muted;
		this.$mute.classList.toggle('is-muted', muted);
		this.$mute.setAttribute('aria-pressed', muted ? 'true' : 'false');
	}

	async play() {
		if (!this.$audio) {
			return;
		}

		this.isRequestedToPlay = true;

		try {
			await this.$audio.play();

			if (!this.isRequestedToPlay) {
				this.$audio.pause();
				return;
			}

			this.updateState(true);
			this.startProgressLoop();
		} catch (error) {
			this.isRequestedToPlay = false;
			this.updateState(false);
		}
	}

	pause() {
		if (!this.$audio) {
			return;
		}

		this.isRequestedToPlay = false;
		this.$audio.pause();
		this.updateState(false);
		this.stopProgressLoop();
	}

	onAudioPlay() {
		if (!this.isRequestedToPlay) {
			this.$audio?.pause();
			return;
		}

		this.updateState(true);
		this.startProgressLoop();
	}

	onAudioPause() {
		this.updateState(false);
		this.stopProgressLoop();
		this.drawWaveform();
	}

	onAudioEnded() {
		this.isRequestedToPlay = false;
		this.updateState(false);
		this.stopProgressLoop();
		this.drawWaveform();
		if (this.$status) {
			this.$status.textContent = 'Audio ended';
		}
	}

	onAudioTimeUpdate() {
		this.updateWaveformA11y();
		this.updateSlider();
		this.drawWaveform();
		this.updateTimeDisplay();
	}

	// Left label = elapsed (counts up), right label = total duration.
	updateTimeDisplay() {
		if (this.$timeCurrent) {
			this.$timeCurrent.textContent = this.formatTime(this.$audio?.currentTime || 0);
		}
		if (this.$timeTotal) {
			const d = this.$audio?.duration;
			this.$timeTotal.textContent = this.formatTime(Number.isFinite(d) ? d : 0);
		}
	}

	formatTime(seconds) {
		const s = Math.max(0, Math.floor(seconds || 0));
		const m = Math.floor(s / 60);
		const r = s % 60;
		return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
	}

	updateState(forcePlaying = null) {
		if (!this.$button || !this.$audio) {
			return;
		}

		const isPlaying = forcePlaying !== null
			? forcePlaying
			: !this.$audio.paused;

		this.classList.toggle('is-playing', isPlaying);
		this.$button.classList.toggle('is-playing', isPlaying);
		this.$button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');

		const svgs = this.$button.querySelectorAll(':scope > svg');
		if (svgs.length >= 2) {
			svgs[0].style.display = isPlaying ? 'none' : 'inline-block';
			svgs[1].style.display = isPlaying ? 'inline-block' : 'none';
		}

		if (this.$button.hasAttribute('data-aria-label-play') && this.$button.hasAttribute('data-aria-label-pause')) {
			this.$button.setAttribute(
			'aria-label',
			isPlaying
				? this.$button.getAttribute('data-aria-label-pause')
				: this.$button.getAttribute('data-aria-label-play')
			);
		}

		if (this.$status) {
			this.$status.textContent = isPlaying ? 'Audio playing' : 'Audio paused';
		}
	}

	setupWaveform() {
		if (!this.$waveform || !this.$audio) {
			return;
		}

		this.$canvas = document.createElement('canvas');
		this.$canvas.setAttribute('aria-hidden', 'true');

		Object.assign(this.$canvas.style, {
			display: 'block',
			width: '100%',
			height: '100%',
		});

		this.$waveform.innerHTML = '';
		this.$waveform.appendChild(this.$canvas);

		this.$waveform.setAttribute('role', 'slider');
		this.$waveform.setAttribute('tabindex', '0');
		this.$waveform.setAttribute('aria-label', this.$waveform.getAttribute('aria-label') || 'Audio timeline');
		this.$waveform.setAttribute('aria-valuemin', '0');
		this.$waveform.setAttribute('aria-valuemax', '100');
		this.$waveform.setAttribute('aria-valuenow', '0');

		this.$waveform.style.cursor = 'pointer';
		this.$waveform.style.touchAction = 'none';

		this.$waveform.addEventListener('pointerdown', this.onWaveformPointerDown);
		this.$waveform.addEventListener('pointermove', this.onWaveformPointerMove);
		this.$waveform.addEventListener('pointerup', this.onWaveformPointerUp);
		this.$waveform.addEventListener('pointercancel', this.onWaveformPointerUp);
		this.$waveform.addEventListener('keydown', this.onKeyDown);

		this.resizeObserver = new ResizeObserver(() => {
			this.resizeCanvas();
			this.drawWaveform();
		});

		this.resizeObserver.observe(this.$waveform);

		this.resizeCanvas();
		this.loadWaveform();
	}

	async loadWaveform() {
		const src = this.$audio.currentSrc || this.$audio.src;

		if (!src || !this.$canvas) {
			return;
		}

		this.$waveform?.classList.add('is-loading');

		try {
			const response = await fetch(src);
			const arrayBuffer = await response.arrayBuffer();

			const AudioContextClass = window.AudioContext || window.webkitAudioContext;
			const audioContext = new AudioContextClass();

			this.audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

			if (audioContext.state !== 'closed') {
				await audioContext.close();
			}

			this.generatePeaks();
			this.drawWaveform();

			this.$waveform?.classList.remove('is-loading');
			this.$waveform?.classList.add('is-loaded');
		} catch (error) {
			this.$waveform?.classList.remove('is-loading');
			this.$waveform?.classList.add('is-error');

			// Fallback: draw a neutral fake waveform instead of leaving the area empty.
			this.generateFallbackPeaks();
			this.drawWaveform();
		}
	}

	generatePeaks() {
		if (!this.audioBuffer || !this.$canvas) {
			return;
		}

		const width = this.$canvas.width || 1;
		const barWidth = Number(this.$waveform?.getAttribute('audio-waveform-bar-width')) || 2;
		const barGap = Number(this.$waveform?.getAttribute('audio-waveform-bar-gap')) || 6;
		const barCount = Math.max(1, Math.floor(width / (barWidth + barGap)));

		const channelData = this.audioBuffer.getChannelData(0);
		const samplesPerBar = Math.floor(channelData.length / barCount);

		this.waveformPeaks = [];

		for (let i = 0; i < barCount; i++) {
			let sum = 0;
			const start = i * samplesPerBar;
			const end = start + samplesPerBar;

			for (let j = start; j < end; j++) {
				sum += Math.abs(channelData[j] || 0);
			}

			const average = sum / samplesPerBar;
			this.waveformPeaks.push(Math.min(1, average * 3.5));
		}

		this.normalizePeaks();
	}

	generateFallbackPeaks() {
		if (!this.$canvas) {
			return;
		}

		const width = this.$canvas.width || 1;
		const barWidth = Number(this.$waveform?.getAttribute('audio-waveform-bar-width')) || 2;
		const barGap = Number(this.$waveform?.getAttribute('audio-waveform-bar-gap')) || 6;
		const barCount = Math.max(1, Math.floor(width / (barWidth + barGap)));

		this.waveformPeaks = Array.from({ length: barCount }, (_, index) => {
			const wave = Math.sin(index * 0.35) * 0.5 + 0.5;
			const detail = Math.sin(index * 1.7) * 0.25 + 0.25;
			return Math.max(0.12, Math.min(1, wave * 0.7 + detail));
		});
	}

	normalizePeaks() {
		const max = Math.max(...this.waveformPeaks);

		if (!max) {
			return;
		}

		this.waveformPeaks = this.waveformPeaks.map((peak) => {
			return Math.max(0.08, peak / max);
		});
	}

	resizeCanvas() {
		if (!this.$canvas || !this.$waveform) {
			return;
		}

		const rect = this.$waveform.getBoundingClientRect();
		const dpr = window.devicePixelRatio || 1;

		this.$canvas.width = Math.max(1, Math.floor(rect.width * dpr));
		this.$canvas.height = Math.max(1, Math.floor(rect.height * dpr));

		this.$canvas.style.width = `${rect.width}px`;
		// this.$canvas.style.height = `${rect.height}px`;

		this.generatePeaks();
	}

	drawWaveform() {
		if (!this.$canvas || !this.$audio) {
			return;
		}

		const ctx = this.$canvas.getContext('2d');
		const width = this.$canvas.width;
		const height = this.$canvas.height;

		if (!ctx || !width || !height) {
			return;
		}

		if (!this.waveformPeaks.length) {
			this.generateFallbackPeaks();
		}

		const progress = this.getProgress();
		const barWidth = (Number(this.$waveform?.getAttribute('audio-waveform-bar-width')) || 2) * (window.devicePixelRatio || 1);
		const barGap = (Number(this.$waveform?.getAttribute('audio-waveform-bar-gap')) || 6) * (window.devicePixelRatio || 1);
		const radius = barWidth / 2;

		let inactiveColor = this.$waveform?.getAttribute('audio-waveform-color') || 'rgba(93, 82, 75, 0.22)';
		let activeColor = this.$waveform?.getAttribute('audio-waveform-progress-color') || 'rgba(93, 82, 75, 0.85)';


		const forcedColors = matchMedia("(forced-colors: active)").matches;
		if (forcedColors) {
			activeColor = "CanvasText";
			inactiveColor = "GrayText";
		}

		ctx.clearRect(0, 0, width, height);

		const totalBarsWidth = this.waveformPeaks.length * barWidth + (this.waveformPeaks.length - 1) * barGap;
		const startX = Math.max(0, (width - totalBarsWidth) / 2);
		const centerY = height / 2;
		const maxBarHeight = height * 0.72;
		const progressX = width * progress;

		this.waveformPeaks.forEach((peak, index) => {
			const x = startX + index * (barWidth + barGap);
			const barHeight = Math.max(barWidth, peak * maxBarHeight);
			const y = centerY - barHeight / 2;

			ctx.fillStyle = x <= progressX ? activeColor : inactiveColor;

			this.roundRect(ctx, x, y, barWidth, barHeight, radius);
			ctx.fill();
		});
	}

	roundRect(ctx, x, y, width, height, radius) {
		const r = Math.min(radius, width / 2, height / 2);

		ctx.beginPath();
		ctx.moveTo(x + r, y);
		ctx.arcTo(x + width, y, x + width, y + height, r);
		ctx.arcTo(x + width, y + height, x, y + height, r);
		ctx.arcTo(x, y + height, x, y, r);
		ctx.arcTo(x, y, x + width, y, r);
		ctx.closePath();
	}

	getProgress() {
		if (!this.$audio || !this.$audio.duration || Number.isNaN(this.$audio.duration)) {
			return 0;
		}

		// When a JSON-driven timeline is active (text reveal runs even with
		// audio paused), use _timelineT so the waveform animates in sync with
		// the text rather than freezing on the audio's actual currentTime.
		if (this._timelineT !== undefined && this._timelineT !== null) {
			return Math.min(1, Math.max(0, this._timelineT / this.$audio.duration));
		}

		return Math.min(1, Math.max(0, this.$audio.currentTime / this.$audio.duration));
	}

	seekFromPointerEvent(event) {
		if (!this.$waveform || !this.$audio || !this.$audio.duration) {
			return;
		}

		const rect = this.$waveform.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const progress = Math.min(1, Math.max(0, x / rect.width));
		const time = progress * this.$audio.duration;

		this.$audio.currentTime = time;

		if (this._timelineT !== undefined && this._timelineT !== null) {
			this._timelineT = time;
		}

		this.updateWaveformA11y();
		this.updateSlider();
		this.drawWaveform();
	}

	onWaveformPointerDown(event) {
		this.isSeeking = true;
		this.$waveform.setPointerCapture?.(event.pointerId);
		this.seekFromPointerEvent(event);
	}

	onWaveformPointerMove(event) {
		if (!this.isSeeking) {
			return;
		}

		this.seekFromPointerEvent(event);
	}

	onWaveformPointerUp(event) {
		this.isSeeking = false;
		this.$waveform?.releasePointerCapture?.(event.pointerId);
	}

	onKeyDown(event) {
		if (!this.$audio || !this.$audio.duration) {
			return;
		}

		const smallStep = 5;
		const largeStep = 15;

		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			this.$audio.currentTime = Math.max(0, this.$audio.currentTime - smallStep);
		}

		if (event.key === 'ArrowRight') {
			event.preventDefault();
			this.$audio.currentTime = Math.min(this.$audio.duration, this.$audio.currentTime + smallStep);
		}

		if (event.key === 'Home') {
			event.preventDefault();
			this.$audio.currentTime = 0;
		}

		if (event.key === 'End') {
			event.preventDefault();
			this.$audio.currentTime = this.$audio.duration;
		}

		if (event.key === 'PageUp') {
			event.preventDefault();
			this.$audio.currentTime = Math.min(this.$audio.duration, this.$audio.currentTime + largeStep);
		}

		if (event.key === 'PageDown') {
			event.preventDefault();
			this.$audio.currentTime = Math.max(0, this.$audio.currentTime - largeStep);
		}

		this.updateWaveformA11y();
		this.drawWaveform();
	}

	updateWaveformA11y() {
		if (!this.$waveform || !this.$audio || !this.$audio.duration) {
			return;
		}

		const value = Math.round(this.getProgress() * 100);

		this.$waveform.setAttribute('aria-valuenow', String(value));
		this.$waveform.setAttribute('aria-valuetext', `${value}%`);
	}

	startProgressLoop() {
		this.stopProgressLoop();
		const loop = () => {
			this.drawWaveform();
			this.updateWaveformA11y();
			this.updateSlider();
			this.currentTime = this.$audio?.currentTime;
			if (this.$audio && !this.$audio.paused) {
				this.rafId = requestAnimationFrame(loop);
			}
		};
		this.rafId = requestAnimationFrame(loop);
	}

	stopProgressLoop() {
		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	}
}

customElements.define('core-audio', CoreAudio);